import React, { useEffect, useMemo, useState } from 'react';
import { useI18n } from '../../contexts/I18nContext';
import { useClientAccess } from '../../hooks/useClientAccess';
import { clientService } from '../../services/clientService';
import { Client } from '../../types/client';
import { deviceTransferService } from '../../services/deviceTransferService';
import { ClientRef, DeviceTransfer, TransferResolved } from '../../types/deviceTransfer';
import { clientPathLabel, indexClients } from '../../utils/clientTree';
import { parseImeis } from '../../utils/imeis';

interface CreateTransferModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** The requests opened: one per current owner. */
  onCreated: (transfers: DeviceTransfer[]) => void;
}

export { parseImeis } from '../../utils/imeis';

/**
 * A new take-over request, in three steps on one screen: the gateways
 * (checked against Provisioning, which names their owner unless the
 * environment hides it), the template they will follow, the message every
 * administrator of the owner receives. The requester learns how many
 * administrators there are, never who.
 *
 * Who takes the gateways: ANEP names the client; a ClientAdmin asks for
 * their own, and names the agency when they run several (a head office
 * holds no gateways, its agencies do).
 */
export const CreateTransferModal: React.FC<CreateTransferModalProps> = ({ isOpen, onClose, onCreated }) => {
  const { t } = useI18n();
  const { isAnepStaff, isClientAdmin } = useClientAccess();
  const [clients, setClients] = useState<Client[]>([]);
  const [toClientId, setToClientId] = useState('');
  const [imeiText, setImeiText] = useState('');
  const [resolved, setResolved] = useState<TransferResolved | null>(null);
  const [checking, setChecking] = useState(false);
  const [templateId, setTemplateId] = useState('');
  const [message, setMessage] = useState('');
  const [errors, setErrors] = useState<{ client?: string; imeis?: string; template?: string; message?: string }>({});
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!isOpen || !(isAnepStaff || isClientAdmin)) return;
    let cancelled = false;
    clientService.fetchAllClients().then((list) => { if (!cancelled) setClients(list); }).catch(() => undefined);
    return () => { cancelled = true; };
  }, [isOpen, isAnepStaff, isClientAdmin]);

  // The clients that may take gateways: the active ones without agencies (the
  // list is already the caller's scope: everything for ANEP, their own for a ClientAdmin).
  const byId = useMemo(() => indexClients(clients), [clients]);
  const takers = useMemo(() => {
    const headOffices = new Set(clients.map((c) => c.parent_id).filter(Boolean));
    return clients.filter((c) => c.active !== false && !headOffices.has(c.id)).sort((a, b) => a.name.localeCompare(b.name));
  }, [clients]);
  const asksTaker = isAnepStaff || takers.length > 1;

  // A ClientAdmin with one candidate: it is theirs, no question asked.
  useEffect(() => {
    if (!isAnepStaff && takers.length === 1) setToClientId(takers[0].id);
  }, [isAnepStaff, takers]);

  const takerLabel = (c: Client): string => clientPathLabel(c, byId);
  const ownerLabel = (c: ClientRef | null | undefined): string => c?.name ?? (c?.id ? c.id : t('portability.hiddenOwner'));
  const fromLabel = (data: TransferResolved): string =>
    data.fromClients.length > 0 ? data.fromClients.map(ownerLabel).join(', ') : ownerLabel(data.fromClient);

  const imeis = parseImeis(imeiText);
  const blocked = resolved !== null && resolved.problems.length > 0;
  const ready = resolved !== null && !blocked;

  const check = async () => {
    if (asksTaker && !toClientId) {
      setErrors({ client: t('portability.create.validation.client') });
      return;
    }
    if (imeis.length === 0) {
      setErrors({ imeis: t('portability.create.validation.imeis') });
      return;
    }
    setErrors({});
    setError(null);
    setChecking(true);
    try {
      const data = await deviceTransferService.resolve(imeis, toClientId || undefined);
      setResolved(data);
      const preferred = data.templates.find((tpl) => tpl.isDefault) ?? data.templates[0];
      setTemplateId(preferred ? String(preferred.id) : '');
    } catch (err: any) {
      setError(err?.response?.data?.detail || err.message || t('portability.create.failed'));
    } finally {
      setChecking(false);
    }
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ready) {
      setErrors({ imeis: t('portability.create.validation.imeis') });
      return;
    }
    const found: typeof errors = {};
    if (!templateId) found.template = t('portability.create.validation.template');
    if (!message.trim()) found.message = t('portability.create.validation.message');
    setErrors(found);
    if (Object.keys(found).length > 0) return;

    setSending(true);
    setError(null);
    try {
      const created = await deviceTransferService.create({
        device_ids: imeis,
        to_client_id: toClientId || undefined,
        template_id: Number(templateId),
        message: message.trim(),
      });
      onCreated(created.items);
      close(true);
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : detail?.problems ? detail.problems.map((p: string) => t(`portability.create.problems.${p}` as any)).join(' ') : err.message || t('portability.create.failed'));
    } finally {
      setSending(false);
    }
  };

  const close = (force = false) => {
    if ((sending || checking) && !force) return;
    setImeiText('');
    setToClientId('');
    setResolved(null);
    setTemplateId('');
    setMessage('');
    setErrors({});
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  const summary = ready
    ? t('portability.create.summary', { count: resolved!.devices.length, type: resolved!.deviceType ?? '', from: fromLabel(resolved!), to: resolved!.toClient.name ?? '' })
    : '';

  return (
    <div className="modal-overlay" onClick={() => close()}>
      <div className="config-template-modal tpc ptc" data-testid="modal-create-transfer" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title tpd-title">
            <div className="tpd-title-text">
              <h2>{t('portability.create.title')}</h2>
              <div className="tpd-title-sub"><span>{t('portability.create.subtitle')}</span></div>
            </div>
          </div>
          <button type="button" className="close-button" data-testid="btn-modal-close" onClick={() => close()} disabled={sending} aria-label={t('common.close')}>×</button>
        </div>

        <form onSubmit={submit} noValidate className="tpc-form">
          <div className="modal-content tpd-body">
            {error && <div className="error-message" role="alert">{error}</div>}

            {/* 0. Who takes the gateways: ANEP names the client; a ClientAdmin running several agencies names the one. */}
            {asksTaker && (
              <section className="cu-section">
                <header className="cu-section-head">
                  <h3>{t(isAnepStaff ? 'portability.create.step0' : 'portability.create.step0Agency')}</h3>
                  <p>{t(isAnepStaff ? 'portability.create.step0Hint' : 'portability.create.step0AgencyHint')}</p>
                </header>
                <div className="cu-field">
                  <select data-testid="field-transfer-client" className={`cu-input${errors.client ? ' is-invalid' : ''}`} value={toClientId} onChange={(e) => { setToClientId(e.target.value); setResolved(null); }} disabled={sending}>
                    <option value="">–</option>
                    {takers.map((c) => <option key={c.id} value={c.id}>{takerLabel(c)}</option>)}
                  </select>
                  {errors.client && <span className="cu-error">{errors.client}</span>}
                </div>
              </section>
            )}

            {/* 1. The gateways */}
            <section className="cu-section">
              <header className="cu-section-head"><h3>{t('portability.create.step1')}</h3></header>
              <div className="cu-field cu-field-wide">
                <label htmlFor="transfer-imeis">{t('portability.create.imeis')}</label>
                <div className="cu-input-row pt-imei-row">
                  <textarea id="transfer-imeis" data-testid="field-transfer-imeis" className={`cu-input mono${errors.imeis ? ' is-invalid' : ''}`} rows={4}
                    value={imeiText} onChange={(e) => { setImeiText(e.target.value); setResolved(null); }} disabled={sending} autoFocus />
                  <button type="button" className="btn btn-secondary btn-sm" data-testid="btn-transfer-check" onClick={check} disabled={checking || sending}>
                    {checking ? t('portability.create.checking') : t('portability.create.check')}
                  </button>
                </div>
                {errors.imeis && <span className="cu-error">{errors.imeis}</span>}
              </div>

              {resolved && (
                <div className="pt-resolved" data-testid="transfer-resolved">
                  {blocked ? (
                    <ul className="pt-problems">
                      {resolved.problems.map((p) => <li key={p} className="cu-error">{t(`portability.create.problems.${p}`)}</li>)}
                    </ul>
                  ) : (
                    <p className="cu-hint is-ok">{summary}</p>
                  )}
                  <ul className="pt-devices">
                    {resolved.devices.map((d) => (
                      <li key={d.idDevice} className={d.found && !d.alreadyMine ? '' : 'is-problem'}>
                        <span className="mono">{d.idDevice}</span>
                        <span className="tpl-muted">
                          {!d.found ? t('portability.create.deviceUnknown') : d.alreadyMine ? t('portability.create.deviceMine') : `${d.deviceType} · ${d.clientName ?? d.clientId ?? t('portability.hiddenOwner')}`}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </section>

            {/* 2. The template, 3. The message: once the gateways are known. */}
            {ready && (
              <>
                <section className="cu-section">
                  <header className="cu-section-head"><h3>{t('portability.create.step2')}</h3><p>{t('portability.create.templateHint')}</p></header>
                  <div className="cu-field">
                    <select data-testid="field-transfer-template" className={`cu-input${errors.template ? ' is-invalid' : ''}`} value={templateId} onChange={(e) => setTemplateId(e.target.value)} disabled={sending}>
                      <option value="">–</option>
                      {resolved!.templates.map((tpl) => (
                        <option key={tpl.id} value={tpl.id}>
                          {tpl.name}{tpl.isDefault ? ` ${t('portability.create.default')}` : ''}{tpl.isRoot ? ` ${t('portability.create.rootTemplate')}` : ''}{tpl.isAnep ? ` ${t('portability.create.anepTemplate')}` : ''}
                        </option>
                      ))}
                    </select>
                    {errors.template && <span className="cu-error">{errors.template}</span>}
                  </div>
                </section>

                <section className="cu-section">
                  <header className="cu-section-head">
                    <h3>{t('portability.create.step3')}</h3>
                    <p data-testid="transfer-recipients">
                      {resolved!.fromClients.length > 1
                        ? t('portability.create.recipientsSeveral', { count: resolved!.fromClients.length, list: resolved!.fromClients.map((c) => `${ownerLabel(c)} (${c.adminCount})`).join(', ') })
                        : t('portability.create.recipients', { count: resolved!.adminCount, client: fromLabel(resolved!) })}
                    </p>
                  </header>
                  <div className="cu-field cu-field-wide">
                    <textarea data-testid="field-transfer-message" className={`cu-input${errors.message ? ' is-invalid' : ''}`} rows={5} maxLength={4000}
                      placeholder={t('portability.create.messagePlaceholder')} value={message} onChange={(e) => setMessage(e.target.value)} disabled={sending} />
                    {errors.message && <span className="cu-error">{errors.message}</span>}
                  </div>
                </section>
              </>
            )}
          </div>

          <div className="modal-actions tpd-footer tpc-footer">
            <div className="tpc-summary">
              {ready ? <strong>{summary}</strong> : <span className="tpd-muted">{t('portability.create.summaryEmpty')}</span>}
            </div>
            <div className="tpc-actions">
              <button type="button" className="btn btn-secondary" data-testid="btn-cancel" onClick={() => close()} disabled={sending}>{t('common.cancel')}</button>
              <button type="submit" className="btn btn-primary" data-testid="btn-submit" disabled={sending || !ready}>
                {sending ? t('portability.create.sending') : t('portability.create.submit')}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};
